import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams, matchPath } from "react-router-dom";
import { useEffect, useState, Suspense } from "react";
import { lazyRetry } from "@/lib/lazyRetry";
import RequirePermission from "@/courier/components/RequirePermission";
import i18n from "@/i18n/config";
import { useTranslation } from "react-i18next";
import { isReservedSlug } from "@/lib/courierStorefront";
import { QuoteProvider } from "@/contexts/QuoteContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import DeveloperAuthGuard from "@/components/auth/DeveloperAuthGuard";


import { useAuthErrorHandler } from "@/hooks/useAuthErrorHandler";
import { ScrollToTop } from "@/components/ScrollToTop";
import { NativeBackButton } from "@/components/NativeBackButton";
// Non-critical chrome: lazy + rendered only after the browser goes idle, so
// none of it lands in the entry chunk or competes with the first paint.
const CookieConsentBanner = lazyRetry(
  () => import("@/components/CookieConsent").then((m) => ({ default: m.CookieConsentBanner })),
  "CookieConsent"
);
const LanguageSuggestionBanner = lazyRetry(
  () => import("@/components/LanguageSuggestionBanner"),
  "LanguageSuggestionBanner"
);
import GlobalErrorBoundary from "@/components/GlobalErrorBoundary";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import { MalakProvider } from "@/providers/MalakProvider";
// Malak chat UI is heavy — split it out of the entry chunk.
const MalakChat = lazyRetry(() => import("@/components/ai/MalakChat"), "MalakChat");

const PushNotificationBanner = lazyRetry(
  () => import("@/components/PushNotificationBanner"),
  "PushNotificationBanner"
);
import { onIdleOnce, runAfterLcp } from "@/lib/afterIdle";
import { prefetchLikelyRoutes } from "@/lib/routePrefetch";
import { hydrateQueryCache, startQueryPersistence } from "@/lib/queryPersist";
import { useProactiveMalak } from "@/hooks/useProactiveMalak";
import { useCrmMembership } from "@/crm/hooks/useCrmMembership";
import { useGlobalChromeHidden } from "@/components/chromeVisibility";
import Layout from "@/components/Layout";
import LanguageRoute from "@/components/LanguageRoute";
import { SUPPORTED_LANGUAGES } from "@/i18n/config";

// Legacy redirect: /c/:slug → /:slug (old storefront URLs).
// Query + hash must survive the hop, otherwise `?preview=1` is lost and the
// owner's unpublished storefront renders as "Profile not found".
const LegacyStorefrontRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  return <Navigate to={`/${slug ?? ""}${location.search}${location.hash}`} replace />;
};


// ── Service Worker + Push Registration ──────────────────────────
import { useServiceWorker } from "@/hooks/useServiceWorker";

// ── Malak Chat with Proactive Triggers ──────────────────────────
const MalakChatWithProactive = () => {
  useProactiveMalak();
  useServiceWorker();
  const location = useLocation();
  const { data: crmMembership } = useCrmMembership();
  // Any page wearing courier-storefront chrome (public storefront, branded
  // guest offer page) is NOT a EuroCollis surface — Malak is a marketplace
  // tool and must never appear inside a courier's brand.
  const storefrontChrome = useGlobalChromeHidden();

  const hasEmbeddedMalak = [
    "/courier/trips/:id/execute",
    "/support-portal/ask-malak",
  ].some((path) => matchPath({ path, end: true }, location.pathname));

  if (hasEmbeddedMalak) return null;
  if (storefrontChrome) return null;
  // Token-scoped guest offer pages belong to the courier journey.
  if (location.pathname.startsWith("/offer/")) return null;
  // Hide Malak on public courier storefronts (single-segment slug routes,
  // same dispatch rule as LanguageRoute).
  const segments = location.pathname.split("/").filter(Boolean);
  if (
    segments.length === 1 &&
    !(SUPPORTED_LANGUAGES as readonly string[]).includes(segments[0]) &&
    !isReservedSlug(segments[0])
  ) {
    return null;
  }
  // Hide Malak for active CRM users (sales portal has its own tools).
  if (crmMembership?.is_active) return null;

  return (
    <Suspense fallback={null}>
      <DeferredMalakChat />
    </Suspense>
  );
};

/**
 * Keeps the chat bundle off the critical path: the widget only mounts once the
 * browser is idle after first paint, or immediately on the first real user
 * interaction (whichever comes first). Behaviour once mounted is unchanged.
 */
const DeferredMalakChat = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;
    let done = false;
    const arm = () => {
      if (done) return;
      done = true;
      setReady(true);
    };
    const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, arm, { once: true, passive: true }));
    // Wait for the page's largest paint first: on throttled mobile the browser
    // reports "idle" while the visible text is still waiting for its web font.
    let cancelIdle: (() => void) | null = null;
    const afterPaint = runAfterLcp(() => {
      cancelIdle = onIdleOnce(arm, 2500);
    });
    return () => {
      done = true;
      afterPaint.cancel();
      cancelIdle?.();
      events.forEach((e) => window.removeEventListener(e, arm));
    };
  }, [ready]);

  if (!ready) return null;
  return <MalakChat />;
};

// ── Skeleton Fallbacks ──────────────────────────────────────────
import { ChatListSkeleton } from "@/components/ui/PageSkeleton";

import { PublicRouteLoader as PageLoader, PortalRouteLoader as PortalSkeleton } from "@/components/loading/RouteLoaders";

const ChatSkeleton = () => (
  <div className="p-4 max-w-4xl mx-auto">
    <ChatListSkeleton />
  </div>
);

// ── Eagerly loaded landing (LCP-critical) ───────────────────────
import Index from "./pages/Index";
import { getStorefrontSubdomain, getStorefrontCustomHost } from "@/lib/storefrontHost";

// ── Lazy core funnel (large bundles split out) ──────────────────
const Quote = lazyRetry(() => import("./pages/Quote"), "Quote");
const VerifyDocument = lazyRetry(() => import("./pages/VerifyDocument"), "VerifyDocument");
const TrackBooking = lazyRetry(() => import("./pages/TrackBooking"), "TrackBooking");
const TrackLanding = lazyRetry(() => import("./pages/TrackLanding"), "TrackLanding");
const VerifyManifest = lazyRetry(() => import("./pages/VerifyManifest"), "VerifyManifest");
const DevelopersPlayground = lazyRetry(() => import("./pages/DevelopersPlayground"), "DevelopersPlayground");
const DevelopersPortal = lazyRetry(() => import("./pages/DevelopersPortal"), "DevelopersPortal");
const McpDocs = lazyRetry(() => import("./pages/McpDocs"), "McpDocs");
const McpInstall = lazyRetry(() => import("./pages/McpInstall"), "McpInstall");

const TrustCenter = lazyRetry(() => import("./pages/TrustCenter"), "TrustCenter");
const DevelopersDashboard = lazyRetry(() => import("./pages/DevelopersDashboard"), "DevelopersDashboard");
const DevelopersAuth = lazyRetry(() => import("./pages/DevelopersAuth"), "DevelopersAuth");
const StorefrontSignIn = lazyRetry(() => import("./pages/StorefrontSignIn"), "StorefrontSignIn");
const StorefrontAuthContinue = lazyRetry(() => import("./pages/StorefrontAuthContinue"), "StorefrontAuthContinue");
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"), "ResetPassword");
const AuthConfirm = lazyRetry(() => import("./pages/AuthConfirm"), "AuthConfirm");
const CourierSetup = lazyRetry(() => import("./pages/CourierSetup"), "CourierSetup");
const ForgotPassword = lazyRetry(() => import("./pages/ForgotPassword"), "ForgotPassword");
const StatusPage = lazyRetry(() => import("./pages/StatusPage"), "StatusPage");
const OpenApiDocs = lazyRetry(() => import("./pages/OpenApiDocs"), "OpenApiDocs");
const Results = lazyRetry(() => import("./pages/Results"), "Results");
const DesignPreviewTransporter = lazyRetry(() => import("./pages/DesignPreviewTransporter"), "DesignPreviewTransporter");
const HowItWorks = lazyRetry(() => import("./pages/HowItWorks"), "HowItWorks");
const About = lazyRetry(() => import("./pages/About"), "About");
const FAQ = lazyRetry(() => import("./pages/FAQ"), "FAQ");
const ForCouriers = lazyRetry(() => import("./pages/ForCouriers"), "ForCouriers");
const CourierOsLanding = lazyRetry(() => import("./pages/CourierOsLanding"), "CourierOsLanding");
const CourierOsCountryPage = lazyRetry(() => import("./pages/CourierOsCountryPage"), "CourierOsCountryPage");



// ── Lazy Public Pages (less visited) ────────────────────────────
const Booking = lazyRetry(() => import("./pages/Booking"), "Booking");
const BookingSuccess = lazyRetry(() => import("./pages/BookingSuccess"), "BookingSuccess");
const Confirmation = lazyRetry(() => import("./pages/Confirmation"), "Confirmation");
const Support = lazyRetry(() => import("./pages/Support"), "Support");
const CustomRequest = lazyRetry(() => import("./pages/CustomRequest"), "CustomRequest");
const CustomRequestSubmitted = lazyRetry(() => import("./pages/CustomRequestSubmitted"), "CustomRequestSubmitted");
const GuestOffers = lazyRetry(() => import("./pages/GuestOffers"), "GuestOffers");
const CustomRequestBooking = lazyRetry(() => import("./pages/CustomRequestBooking"), "CustomRequestBooking");
const ResetPin = lazyRetry(() => import("./pages/ResetPin"), "ResetPin");
const NotFound = lazyRetry(() => import("./pages/NotFound"), "NotFound");
const MalakCardsShowcase = lazyRetry(() => import("./pages/dev/MalakCardsShowcase"), "MalakCardsShowcase");
const DevGalleryShot = lazyRetry(() => import("./pages/DevGalleryShot"), "DevGalleryShot");
const Login = lazyRetry(() => import("./pages/Login"), "Login");

// ── Affiliate program (Phase 1 MVP) ─────────────────────────────
const AffiliateLayout = lazyRetry(() => import("./affiliate/AffiliateLayout"), "AffiliateLayout");
const AffiliateOverview = lazyRetry(() => import("./affiliate/pages/AffiliateOverview"), "AffiliateOverview");
const AffiliateLink = lazyRetry(() => import("./affiliate/pages/AffiliateLink"), "AffiliateLink");
const AffiliateReferrals = lazyRetry(() => import("./affiliate/pages/AffiliateReferrals"), "AffiliateReferrals");
const AffiliateEarnings = lazyRetry(() => import("./affiliate/pages/AffiliateEarnings"), "AffiliateEarnings");
const AffiliateCashout = lazyRetry(() => import("./affiliate/pages/AffiliateCashout"), "AffiliateCashout");
const AffiliateLeaderboard = lazyRetry(() => import("./affiliate/pages/AffiliateLeaderboard"), "AffiliateLeaderboard");
const AffiliateRules = lazyRetry(() => import("./affiliate/pages/AffiliateRules"), "AffiliateRules");
const ReferralRedirect = lazyRetry(() => import("./affiliate/pages/ReferralRedirect"), "ReferralRedirect");
import { captureRefFromUrl, claimReferralIfPresent } from "./affiliate/lib/attributionClaim";
const AffiliateAnalytics = lazyRetry(() => import("./affiliate/pages/AffiliateAnalytics"), "AffiliateAnalytics");
const AffiliateAccount = lazyRetry(() => import("./affiliate/pages/AffiliateAccount"), "AffiliateAccount");
const AffiliateAssets = lazyRetry(() => import("./affiliate/pages/AffiliateAssets"), "AffiliateAssets");
const AffiliatesLanding = lazyRetry(() => import("./pages/AffiliatesLanding"), "AffiliatesLanding");
const AdminAffiliates = lazyRetry(() => import("./admin/pages/AdminAffiliates").then(m => ({ default: m.AdminAffiliates })), "AdminAffiliates");
const AdminAffiliateDetail = lazyRetry(() => import("./admin/pages/AdminAffiliateDetail").then(m => ({ default: m.AdminAffiliateDetail })), "AdminAffiliateDetail");
const AdminPartners = lazyRetry(() => import("./admin/pages/AdminPartners").then(m => ({ default: m.AdminPartners })), "AdminPartners");
const AdminPartnerDetail = lazyRetry(() => import("./admin/pages/AdminPartnerDetail").then(m => ({ default: m.AdminPartnerDetail })), "AdminPartnerDetail");
const AdminAffiliateAssets = lazyRetry(() => import("./admin/pages/AdminAffiliateAssets").then(m => ({ default: m.AdminAffiliateAssets })), "AdminAffiliateAssets");
const AdminAffiliateCashouts = lazyRetry(() => import("./admin/pages/AdminAffiliateCashouts").then(m => ({ default: m.AdminAffiliateCashouts })), "AdminAffiliateCashouts");
const AdminReferralSeasons = lazyRetry(() => import("./admin/pages/AdminReferralSeasons").then(m => ({ default: m.AdminReferralSeasons })), "AdminReferralSeasons");
const SignupSuccess = lazyRetry(() => import("./pages/SignupSuccess"), "SignupSuccess");
const DepositPayment = lazyRetry(() => import("./pages/DepositPayment"), "DepositPayment");
const Notifications = lazyRetry(() => import("./pages/Notifications"), "Notifications");
const Install = lazyRetry(() => import("./pages/Install"), "Install");
const EditRequestApproval = lazyRetry(() => import("./pages/EditRequestApproval"), "EditRequestApproval");
// SEO corpus pages — lazy so the route corpus stays out of the entry chunk.
const ShippingRoutesDirectory = lazyRetry(() => import("./pages/ShippingRoutesDirectory"), "ShippingRoutesDirectory");
const RoutePage = lazyRetry(() => import("./pages/RoutePage"), "RoutePage");
const CountryHubPage = lazyRetry(() => import("./pages/CountryHubPage"), "CountryHubPage");
const DestinationHubPage = lazyRetry(() => import("./pages/DestinationHubPage"), "DestinationHubPage");
const OriginHubPage = lazyRetry(() => import("./pages/OriginHubPage"), "OriginHubPage");
const ShippingRouteDispatcher = lazyRetry(() => import("./pages/ShippingRouteDispatcher"), "ShippingRouteDispatcher");
const ShippingItemRedirect = lazyRetry(() => import("./pages/ShippingItemRedirect"), "ShippingItemRedirect");
const GuidePage = lazyRetry(() => import("./pages/GuidePage"), "GuidePage");
const CourierToCountryPage = lazyRetry(() => import("./pages/CourierToCountryPage"), "CourierToCountryPage");
const HtmlSitemap = lazyRetry(() => import("./pages/HtmlSitemap"), "HtmlSitemap");
const PrerenderedFallback = lazyRetry(() => import("./pages/PrerenderedFallback"), "PrerenderedFallback");
const Press = lazyRetry(() => import("./pages/Press"), "Press");
const BenmiraFoundation = lazyRetry(() => import("./pages/BenmiraFoundation"), "BenmiraFoundation");

const Partnerships = lazyRetry(() => import("./pages/Partnerships"), "Partnerships");
const ScanDelivery = lazyRetry(() => import("./pages/ScanDelivery"), "ScanDelivery");
const TripManifest = lazyRetry(() => import("./pages/TripManifest"), "TripManifest");
const TripLanding = lazyRetry(() => import("./pages/TripLanding"), "TripLanding");
const ShipmentInvite = lazyRetry(() => import("./pages/ShipmentInvite"), "ShipmentInvite");

const ReceiverChat = lazyRetry(() => import("./pages/ReceiverChat"), "ReceiverChat");

// ── Lazy Policy Pages ───────────────────────────────────────────
const PolicyCentre = lazyRetry(() => import("./pages/policy/PolicyCentre"), "PolicyCentre");
const PrivacyPolicy = lazyRetry(() => import("./pages/policy/PrivacyPolicy"), "PrivacyPolicy");
const CookiePolicyPage = lazyRetry(() => import("./pages/policy/CookiePolicy"), "CookiePolicy");
const CustomerTerms = lazyRetry(() => import("./pages/policy/CustomerTerms"), "CustomerTerms");
const CourierTermsPage = lazyRetry(() => import("./pages/policy/CourierTerms"), "CourierTerms");
const ProhibitedItems = lazyRetry(() => import("./pages/policy/ProhibitedItems"), "ProhibitedItems");
const AcceptableUsePolicy = lazyRetry(() => import("./pages/policy/AcceptableUsePolicy"), "AcceptableUsePolicy");
const CustomsPolicy = lazyRetry(() => import("./pages/policy/CustomsPolicy"), "CustomsPolicy");
const AffiliateTermsPolicy = lazyRetry(() => import("./pages/policy/AffiliateTerms"), "AffiliateTerms");
const DPAPolicy = lazyRetry(() => import("./pages/policy/DPA"), "DPA");
const SubProcessorsPolicy = lazyRetry(() => import("./pages/policy/SubProcessors"), "SubProcessors");

// ── Lazy Courier Portal ─────────────────────────────────────────
const CourierLayout = lazyRetry(() => import("./courier/CourierLayout"), "CourierLayout");
const Dashboard = lazyRetry(() => import("./courier/pages/Dashboard"), "Dashboard");
const Trips = lazyRetry(() => import("./courier/pages/Trips"), "Trips");
const TripPerformanceRedirect = lazyRetry(() => import("./courier/pages/TripPerformanceRedirect"), "TripPerformanceRedirect");
const TripDetail = lazyRetry(() => import("./courier/pages/TripDetail"), "TripDetail");
const TripExecute = lazyRetry(() => import("./courier/pages/TripExecute"), "TripExecute");
const CourierAnalytics = lazyRetry(() => import("./courier/pages/Analytics"), "CourierAnalytics");
const CourierFinance = lazyRetry(() => import("./courier/pages/Finance"), "CourierFinance");
const CourierFleet = lazyRetry(() => import("./courier/pages/Fleet"), "CourierFleet");
const DriverHome = lazyRetry(() => import("./driver/DriverHome"), "DriverHome");
const CreateTrip = lazyRetry(() => import("./courier/pages/CreateTrip"), "CreateTrip");
const EditTrip = lazyRetry(() => import("./courier/pages/EditTrip"), "EditTrip");
const CourierBookings = lazyRetry(() => import("./courier/pages/Bookings"), "CourierBookings");
const DirectShipmentNew = lazyRetry(() => import("./courier/pages/DirectShipmentNew"), "DirectShipmentNew");
const CourierContacts = lazyRetry(() => import("./courier/pages/CourierContacts"), "CourierContacts");
const CourierContactDetail = lazyRetry(() => import("./courier/pages/CourierContactDetail"), "CourierContactDetail");

const PricingTemplates = lazyRetry(() => import("./courier/pages/PricingTemplates"), "PricingTemplates");
const CourierTeam = lazyRetry(() => import("./courier/pages/CourierTeam"), "CourierTeam");
const CourierTeamJoin = lazyRetry(() => import("./courier/pages/CourierTeamJoin"), "CourierTeamJoin");
const RouteTemplatesPage = lazyRetry(() => import("./courier/pages/Routes"), "Routes");
const Profile = lazyRetry(() => import("./courier/pages/Profile"), "Profile");
const CourierReviewsPage = lazyRetry(() => import("./courier/pages/Reviews"), "CourierReviewsPage");
const CourierSupport = lazyRetry(() => import("./courier/pages/CourierSupport"), "CourierSupport");
const Jobs = lazyRetry(() => import("./courier/pages/Jobs"), "Jobs");
const JobDetail = lazyRetry(() => import("./courier/pages/JobDetail"), "JobDetail");
const CourierMessages = lazyRetry(() => import("./courier/pages/Messages"), "CourierMessages");
const CourierBookingDetail = lazyRetry(() => import("./courier/pages/CourierBookingDetail"), "CourierBookingDetail");
const OpenDocumentLink = lazyRetry(() => import("./courier/pages/OpenDocumentLink"), "OpenDocumentLink");
const CourierOnboarding = lazyRetry(() => import("./courier/pages/Onboarding"), "CourierOnboarding");
const MatchOfferDetail = lazyRetry(() => import("./courier/pages/MatchOfferDetail"), "MatchOfferDetail");
const CourierNotifications = lazyRetry(() => import("./courier/pages/Notifications"), "CourierNotifications");
const CourierNotificationSettings = lazyRetry(() => import("./courier/pages/NotificationSettings"), "CourierNotificationSettings");
const CourierPromotions = lazyRetry(() => import("./courier/pages/Promotions"), "CourierPromotions");
const CourierGuides = lazyRetry(() => import("./courier/pages/CourierGuides"), "CourierGuides");
const Partners = lazyRetry(() => import("./courier/pages/Partners"), "Partners");
const AgencyAssignments = lazyRetry(() => import("./courier/pages/AgencyAssignments"), "AgencyAssignments");
const OfflineAssignment = lazyRetry(() => import("./pages/OfflineAssignment"), "OfflineAssignment");
const BulkDropoff = lazyRetry(() => import("./courier/pages/BulkDropoff"), "BulkDropoff");
const CourierDocuments = lazyRetry(() => import("./courier/pages/CourierDocuments"), "CourierDocuments");
const CourierSettings = lazyRetry(() => import("./courier/pages/Settings"), "CourierSettings");
const CourierDocumentsTrip = lazyRetry(() => import("./courier/pages/CourierDocumentsTrip"), "CourierDocumentsTrip");

// Partner Portal (pickup partners)
const PartnerLayout = lazyRetry(() => import("./partner/PartnerLayout"), "PartnerLayout");
const PartnerDashboard = lazyRetry(() => import("./partner/pages/PartnerDashboard"), "PartnerDashboard");
const PartnerScan = lazyRetry(() => import("./partner/pages/PartnerScan"), "PartnerScan");
const PartnerHandoffDetail = lazyRetry(() => import("./partner/pages/PartnerHandoffDetail"), "PartnerHandoffDetail");
const PartnerLoad = lazyRetry(() => import("./partner/pages/PartnerLoad"), "PartnerLoad");
const PartnerProfile = lazyRetry(() => import("./partner/pages/PartnerProfile"), "PartnerProfile");
const PartnerStorefront = lazyRetry(() => import("./partner/pages/PartnerStorefront"), "PartnerStorefront");
const PartnerHistory = lazyRetry(() => import("./partner/pages/PartnerHistory"), "PartnerHistory");
const PartnerExceptions = lazyRetry(() => import("./partner/pages/PartnerExceptions"), "PartnerExceptions");

const PartnerCouriers = lazyRetry(() => import("./partner/pages/PartnerCouriers"), "PartnerCouriers");
const PartnerCourierDetail = lazyRetry(() => import("./partner/pages/PartnerCourierDetail"), "PartnerCourierDetail");
const PartnerLogin = lazyRetry(() => import("./partner/pages/PartnerLogin"), "PartnerLogin");
const PartnerPendingApproval = lazyRetry(() => import("./partner/pages/PartnerPendingApproval"), "PartnerPendingApproval");
const PartnerAcceptInvite = lazyRetry(() => import("./partner/pages/PartnerAcceptInvite"), "PartnerAcceptInvite");
const PartnerJoin = lazyRetry(() => import("./partner/pages/PartnerJoin"), "PartnerJoin");
const CustomerPickup = lazyRetry(() => import("./pages/CustomerPickup"), "CustomerPickup");
const ReviewByToken = lazyRetry(() => import("./pages/ReviewByToken"), "ReviewByToken");

// ── Lazy Customer Portal ────────────────────────────────────────
const AccountLayout = lazyRetry(() => import("./account/AccountLayout"), "AccountLayout");
const AccountDashboard = lazyRetry(() => import("./account/pages/AccountDashboard"), "AccountDashboard");
const MyBookings = lazyRetry(() => import("./account/pages/MyBookings"), "MyBookings");
const ShipmentRequests = lazyRetry(() => import("./account/pages/ShipmentRequests"), "ShipmentRequests");
const ShipmentRequestDetail = lazyRetry(() => import("./account/pages/ShipmentRequestDetail"), "ShipmentRequestDetail");
const MyOffers = lazyRetry(() => import("./account/pages/MyOffers"), "MyOffers");
const AccountProfile = lazyRetry(() => import("./account/pages/AccountProfile"), "AccountProfile");
const Messages = lazyRetry(() => import("./account/pages/Messages"), "Messages");
const OfferDetail = lazyRetry(() => import("./account/pages/OfferDetail"), "OfferDetail");
const BookingDetail = lazyRetry(() => import("./account/pages/BookingDetail"), "BookingDetail");
const AccountSupport = lazyRetry(() => import("./account/pages/AccountSupport"), "AccountSupport");
const AccountNotificationSettings = lazyRetry(() => import("./account/pages/NotificationSettings"), "AccountNotificationSettings");
const MyDisputes = lazyRetry(() => import("./account/pages/MyDisputes"), "MyDisputes");
const CustomerDisputeDetail = lazyRetry(() => import("./account/pages/DisputeDetail"), "CustomerDisputeDetail");
const MyTransporters = lazyRetry(() => import("./account/pages/MyTransporters"), "MyTransporters");
const AddressBook = lazyRetry(() => import("./account/pages/AddressBook"), "AddressBook");
const CourierDisputes = lazyRetry(() => import("./courier/pages/CourierDisputes"), "CourierDisputes");
const CourierDisputeDetail = lazyRetry(() => import("./courier/pages/CourierDisputeDetail"), "CourierDisputeDetail");

// ── Lazy Admin Panel ────────────────────────────────────────────
const AdminLayout = lazyRetry(() => import("./admin/AdminLayout").then(m => ({ default: m.AdminLayout })), "AdminLayout");
const AdminDashboard = lazyRetry(() => import("./admin/pages/AdminDashboard").then(m => ({ default: m.AdminDashboard })), "AdminDashboard");
const AdminUsers = lazyRetry(() => import("./admin/pages/AdminUsers").then(m => ({ default: m.AdminUsers })), "AdminUsers");
const AdminCouriers = lazyRetry(() => import("./admin/pages/AdminCouriers").then(m => ({ default: m.AdminCouriers })), "AdminCouriers");
const AdminRoutes = lazyRetry(() => import("./admin/pages/AdminRoutes").then(m => ({ default: m.AdminRoutes })), "AdminRoutes");
const AdminExceptions = lazyRetry(() => import("./admin/pages/AdminExceptions").then(m => ({ default: m.AdminExceptions })), "AdminExceptions");
const AdminCrm = lazyRetry(() => import("./admin/pages/AdminCrm").then(m => ({ default: m.AdminCrm })), "AdminCrm");
const AdminTeams = lazyRetry(() => import("./admin/pages/AdminTeams").then(m => ({ default: m.AdminTeams })), "AdminTeams");
const AdminDomains = lazyRetry(() => import("./admin/pages/AdminDomains"), "AdminDomains");
const AdminStorefronts = lazyRetry(() => import("./admin/pages/AdminStorefronts").then(m => ({ default: m.AdminStorefronts })), "AdminStorefronts");
const AdminContacts = lazyRetry(() => import("./admin/pages/AdminContacts").then(m => ({ default: m.AdminContacts })), "AdminContacts");
const AdminLeads = lazyRetry(() => import("./admin/pages/AdminLeads").then(m => ({ default: m.AdminLeads })), "AdminLeads");
const AdminReconciliation = lazyRetry(() => import("./admin/pages/AdminReconciliation").then(m => ({ default: m.AdminReconciliation })), "AdminReconciliation");
const AdminRouteDetail = lazyRetry(() => import("./admin/pages/AdminRouteDetail").then(m => ({ default: m.AdminRouteDetail })), "AdminRouteDetail");
const AdminPricingTemplates = lazyRetry(() => import("./admin/pages/AdminPricingTemplates").then(m => ({ default: m.AdminPricingTemplates })), "AdminPricingTemplates");
const AdminPricingTemplateDetail = lazyRetry(() => import("./admin/pages/AdminPricingTemplateDetail").then(m => ({ default: m.AdminPricingTemplateDetail })), "AdminPricingTemplateDetail");
const AdminTrips = lazyRetry(() => import("./admin/pages/AdminTrips").then(m => ({ default: m.AdminTrips })), "AdminTrips");
const AdminTripDetail = lazyRetry(() => import("./admin/pages/AdminTripDetail").then(m => ({ default: m.AdminTripDetail })), "AdminTripDetail");
const AdminRequests = lazyRetry(() => import("./admin/pages/AdminRequests").then(m => ({ default: m.AdminRequests })), "AdminRequests");
const AdminRequestDetail = lazyRetry(() => import("./admin/pages/AdminRequestDetail").then(m => ({ default: m.AdminRequestDetail })), "AdminRequestDetail");
const AdminBids = lazyRetry(() => import("./admin/pages/AdminBids").then(m => ({ default: m.AdminBids })), "AdminBids");
const AdminBidDetail = lazyRetry(() => import("./admin/pages/AdminBidDetail").then(m => ({ default: m.AdminBidDetail })), "AdminBidDetail");
const AdminBookings = lazyRetry(() => import("./admin/pages/AdminBookings").then(m => ({ default: m.AdminBookings })), "AdminBookings");
const AdminBookingDetail = lazyRetry(() => import("./admin/pages/AdminBookingDetail").then(m => ({ default: m.AdminBookingDetail })), "AdminBookingDetail");
const AdminUserDetail = lazyRetry(() => import("./admin/pages/AdminUserDetail").then(m => ({ default: m.AdminUserDetail })), "AdminUserDetail");
const AdminCourierDetail = lazyRetry(() => import("./admin/pages/AdminCourierDetail").then(m => ({ default: m.AdminCourierDetail })), "AdminCourierDetail");
const AdminCurrencies = lazyRetry(() => import("./admin/pages/AdminCurrencies").then(m => ({ default: m.AdminCurrencies })), "AdminCurrencies");
const AdminPayments = lazyRetry(() => import("./admin/pages/AdminPayments").then(m => ({ default: m.AdminPayments })), "AdminPayments");
const AdminPaymentDetail = lazyRetry(() => import("./admin/pages/AdminPaymentDetail").then(m => ({ default: m.AdminPaymentDetail })), "AdminPaymentDetail");
const AdminDisputes = lazyRetry(() => import("./admin/pages/AdminDisputes").then(m => ({ default: m.AdminDisputes })), "AdminDisputes");
const AdminDisputeDetail = lazyRetry(() => import("./admin/pages/AdminDisputeDetail").then(m => ({ default: m.AdminDisputeDetail })), "AdminDisputeDetail");
const AdminSupportTickets = lazyRetry(() => import("./admin/pages/AdminSupportTickets").then(m => ({ default: m.AdminSupportTickets })), "AdminSupportTickets");
const AdminTicketDetail = lazyRetry(() => import("./admin/pages/AdminTicketDetail").then(m => ({ default: m.AdminTicketDetail })), "AdminTicketDetail");
const AdminSupportAnalytics = lazyRetry(() => import("./admin/pages/AdminSupportAnalytics").then(m => ({ default: m.AdminSupportAnalytics })), "AdminSupportAnalytics");
const AdminOpeningHours = lazyRetry(() => import("./admin/pages/AdminOpeningHours").then(m => ({ default: m.AdminOpeningHours })), "AdminOpeningHours");
const AdminLogs = lazyRetry(() => import("./admin/pages/AdminLogs").then(m => ({ default: m.AdminLogs })), "AdminLogs");
const AdminScanLogs = lazyRetry(() => import("./admin/pages/AdminScanLogs").then(m => ({ default: m.AdminScanLogs })), "AdminScanLogs");
const AdminAnalytics = lazyRetry(() => import("./admin/pages/AdminAnalytics").then(m => ({ default: m.AdminAnalytics })), "AdminAnalytics");
const AdminReviews = lazyRetry(() => import("./admin/pages/AdminReviews").then(m => ({ default: m.AdminReviews })), "AdminReviews");
const AdminApplications = lazyRetry(() => import("./admin/pages/AdminApplications").then(m => ({ default: m.AdminApplications })), "AdminApplications");
const AdminDocuments = lazyRetry(() => import("./admin/pages/AdminDocuments").then(m => ({ default: m.AdminDocuments })), "AdminDocuments");
const AdminSettings = lazyRetry(() => import("./admin/pages/AdminSettings").then(m => ({ default: m.AdminSettings })), "AdminSettings");
const AdminItems = lazyRetry(() => import("./admin/pages/AdminItems").then(m => ({ default: m.AdminItems })), "AdminItems");
const AdminAILearning = lazyRetry(() => import("./admin/pages/AdminAILearning").then(m => ({ default: m.AdminAILearning })), "AdminAILearning");
const AdminMalakOps = lazyRetry(() => import("./admin/pages/AdminMalakOps").then(m => ({ default: m.AdminMalakOps })), "AdminMalakOps");
const AdminCostCounters = lazyRetry(() => import("./admin/pages/AdminCostCounters").then(m => ({ default: m.AdminCostCounters })), "AdminCostCounters");
const AdminAICapMonitor = lazyRetry(() => import("./admin/pages/AdminAICapMonitor").then(m => ({ default: m.AdminAICapMonitor })), "AdminAICapMonitor");
const AdminMalakSettings = lazyRetry(() => import("./admin/pages/AdminMalakSettings").then(m => ({ default: m.AdminMalakSettings })), "AdminMalakSettings");
const AdminCSAT = lazyRetry(() => import("./admin/pages/AdminCSAT").then(m => ({ default: m.AdminCSAT })), "AdminCSAT");
// AdminLearningPipe / AdminKBReviewQueue merged into AdminAILearning hub as tabs (see /admin/ai-learning).
const AdminCorrectionModeration = lazyRetry(() => import("./admin/pages/AdminCorrectionModeration").then(m => ({ default: m.AdminCorrectionModeration })), "AdminCorrectionModeration");
const AdminCostAttribution = lazyRetry(() => import("./admin/pages/AdminCostAttribution").then(m => ({ default: m.AdminCostAttribution })), "AdminCostAttribution");
const AdminAuditTrail = lazyRetry(() => import("./admin/pages/AdminAuditTrail").then(m => ({ default: m.AdminAuditTrail })), "AdminAuditTrail");
const AdminAIMargin = lazyRetry(() => import("./admin/pages/AdminAIMargin").then(m => ({ default: m.AdminAIMargin })), "AdminAIMargin");
const AdminMalakRegression = lazyRetry(() => import("./admin/pages/AdminMalakRegression").then(m => ({ default: m.AdminMalakRegression })), "AdminMalakRegression");
const AdminItemsCatalog = lazyRetry(() => import("./admin/pages/AdminItemsCatalog").then(m => ({ default: m.AdminItemsCatalog })), "AdminItemsCatalog");
const AdminEditModeration = lazyRetry(() => import("./admin/pages/AdminEditModeration").then(m => ({ default: m.AdminEditModeration })), "AdminEditModeration");
const AdminFieldPolicies = lazyRetry(() => import("./admin/pages/AdminFieldPolicies").then(m => ({ default: m.AdminFieldPolicies })), "AdminFieldPolicies");
const AdminKBFreshness = lazyRetry(() => import("./admin/pages/AdminKBFreshness").then(m => ({ default: m.AdminKBFreshness })), "AdminKBFreshness");
const AdminKBRetrieval = lazyRetry(() => import("./admin/pages/AdminKBRetrieval").then(m => ({ default: m.AdminKBRetrieval })), "AdminKBRetrieval");
const AdminMalakTrace = lazyRetry(() => import("./admin/pages/AdminMalakTrace").then(m => ({ default: m.AdminMalakTrace })), "AdminMalakTrace");
// AdminLiveChat deleted — superseded by AdminLiveSupport.
const AdminLiveSupport = lazyRetry(() => import("./admin/pages/AdminLiveSupport").then(m => ({ default: m.AdminLiveSupport })), "AdminLiveSupport");
const AdminSupervisor = lazyRetry(() => import("./admin/pages/AdminSupervisor").then(m => ({ default: m.AdminSupervisor })), "AdminSupervisor");
const AdminStressTest = lazyRetry(() => import("./admin/pages/AdminStressTest").then(m => ({ default: m.AdminStressTest })), "AdminStressTest");
const AdminWebhookRetries = lazyRetry(() => import("./admin/pages/AdminWebhookRetries").then(m => ({ default: m.AdminWebhookRetries })), "AdminWebhookRetries");
const AdminPromoCodes = lazyRetry(() => import("./admin/pages/AdminPromoCodes").then(m => ({ default: m.AdminPromoCodes })), "AdminPromoCodes");
const AdminEmails = lazyRetry(() => import("./admin/pages/AdminEmails").then(m => ({ default: m.AdminEmails })), "AdminEmails");
const AdminLocations = lazyRetry(() => import("./admin/pages/AdminLocations").then(m => ({ default: m.AdminLocations })), "AdminLocations");
const AdminCorridors = lazyRetry(() => import("./admin/pages/AdminCorridors").then(m => ({ default: m.AdminCorridors })), "AdminCorridors");
const AdminNotifications = lazyRetry(() => import("./admin/pages/AdminNotifications").then(m => ({ default: m.AdminNotifications })), "AdminNotifications");
// AdminMonitoring embedded as "System" tab inside AdminMonitor — no standalone route.
const AdminModeration = lazyRetry(() => import("./admin/pages/AdminModeration").then(m => ({ default: m.AdminModeration })), "AdminModeration");
const AdminAISecurity = lazyRetry(() => import("./admin/pages/AdminAISecurity").then(m => ({ default: m.AdminAISecurity })), "AdminAISecurity");
const AdminMonitor = lazyRetry(() => import("./admin/pages/AdminMonitor").then(m => ({ default: m.AdminMonitor })), "AdminMonitor");
const AdminMalakChats = lazyRetry(() => import("./admin/pages/AdminMalakChats").then(m => ({ default: m.AdminMalakChats })), "AdminMalakChats");
const AdminMalakFunnel = lazyRetry(() => import("./admin/pages/AdminMalakFunnel").then(m => ({ default: m.AdminMalakFunnel })), "AdminMalakFunnel");
const AdminFoundation = lazyRetry(() => import("./admin/pages/AdminFoundation").then(m => ({ default: m.AdminFoundation })), "AdminFoundation");

// ── Lazy Support Portal ─────────────────────────────────────────
const SupportPortalLayout = lazyRetry(() => import("./support/SupportPortalLayout"), "SupportPortalLayout");
const SupportDashboard = lazyRetry(() => import("./support/pages/SupportDashboard").then(m => ({ default: m.SupportDashboard })), "SupportDashboard");
const SupportCustomers = lazyRetry(() => import("./support/pages/SupportCustomers").then(m => ({ default: m.SupportCustomers })), "SupportCustomers");
const SupportCouriers = lazyRetry(() => import("./support/pages/SupportCouriers").then(m => ({ default: m.SupportCouriers })), "SupportCouriers");
const SupportBookings = lazyRetry(() => import("./support/pages/SupportBookings").then(m => ({ default: m.SupportBookings })), "SupportBookings");
const SupportRequests = lazyRetry(() => import("./support/pages/SupportRequests").then(m => ({ default: m.SupportRequests })), "SupportRequests");
const SupportRequestDetail = lazyRetry(() => import("./support/pages/SupportRequestDetail").then(m => ({ default: m.SupportRequestDetail })), "SupportRequestDetail");
const SupportBids = lazyRetry(() => import("./support/pages/SupportBids").then(m => ({ default: m.SupportBids })), "SupportBids");
const SupportTrips = lazyRetry(() => import("./support/pages/SupportTrips").then(m => ({ default: m.SupportTrips })), "SupportTrips");
const SupportRoutes = lazyRetry(() => import("./support/pages/SupportRoutes").then(m => ({ default: m.SupportRoutes })), "SupportRoutes");
const SupportTickets = lazyRetry(() => import("./support/pages/SupportTickets").then(m => ({ default: m.SupportTickets })), "SupportTickets");
const SupportTicketDetail = lazyRetry(() => import("./support/pages/SupportTicketDetail").then(m => ({ default: m.SupportTicketDetail })), "SupportTicketDetail");
const SupportConversations = lazyRetry(() => import("./support/pages/SupportConversations").then(m => ({ default: m.SupportConversations })), "SupportConversations");
const SupportPayments = lazyRetry(() => import("./support/pages/SupportPayments").then(m => ({ default: m.SupportPayments })), "SupportPayments");
const SupportDisputes = lazyRetry(() => import("./support/pages/SupportDisputes").then(m => ({ default: m.SupportDisputes })), "SupportDisputes");
const SupportLiveSupport = lazyRetry(() => import("./support/pages/SupportLiveSupport").then(m => ({ default: m.SupportLiveSupport })), "SupportLiveSupport");
const SupportAgentAnalytics = lazyRetry(() => import("./support/pages/SupportAgentAnalytics"), "SupportAgentAnalytics");
const SupportBlockedUsers = lazyRetry(() => import("./support/pages/SupportBlockedUsers"), "SupportBlockedUsers");
const SupportAskMalak = lazyRetry(() => import("./support/pages/SupportAskMalak"), "SupportAskMalak");

const SupportCustomerDetail = lazyRetry(() => import("./support/pages/SupportCustomerDetail").then(m => ({ default: m.SupportCustomerDetail })), "SupportCustomerDetail");
const SupportCourierDetail = lazyRetry(() => import("./support/pages/SupportCourierDetail").then(m => ({ default: m.SupportCourierDetail })), "SupportCourierDetail");
const SupportBookingDetail = lazyRetry(() => import("./support/pages/SupportBookingDetail").then(m => ({ default: m.SupportBookingDetail })), "SupportBookingDetail");
const SupportDisputeDetail = lazyRetry(() => import("./support/pages/SupportDisputeDetail").then(m => ({ default: m.SupportDisputeDetail })), "SupportDisputeDetail");
const SupportPaymentDetail = lazyRetry(() => import("./support/pages/SupportPaymentDetail").then(m => ({ default: m.SupportPaymentDetail })), "SupportPaymentDetail");
const SupportConversationDetail = lazyRetry(() => import("./support/pages/SupportConversationDetail").then(m => ({ default: m.SupportConversationDetail })), "SupportConversationDetail");
const SupportVerificationFunnel = lazyRetry(() => import("./support/pages/SupportVerificationFunnel"), "SupportVerificationFunnel");

// ── Lazy Public Courier Profile ────────────────────────────────
const CourierPublicProfile = lazyRetry(() => import("./pages/CourierPublicProfile"), "CourierPublicProfile");
const CourierStorefront = lazyRetry(() => import("./pages/CourierStorefront"), "CourierStorefront");
/** On a courier's own address (name.eurocollis.com or their domain) the home page is their storefront, never the EuroCollis homepage. */
const HostHome = () =>
  getStorefrontSubdomain() || getStorefrontCustomHost()
    ? <Suspense fallback={<PageLoader />}><CourierStorefront /></Suspense>
    : <Index />;
const StorefrontCorridor = lazyRetry(() => import("./pages/StorefrontCorridor"), "StorefrontCorridor");
const StorefrontTrack = lazyRetry(() => import("./pages/StorefrontTrack"), "StorefrontTrack");
const StorefrontBuilder = lazyRetry(() => import("./courier/pages/StorefrontBuilder"), "StorefrontBuilder");
const PublicQuote = lazyRetry(() => import("./pages/PublicQuote"), "PublicQuote");
const PublicBillingDocument = lazyRetry(() => import("./pages/PublicBillingDocument"), "PublicBillingDocument");
/**
 * A courier's own address (name.eurocollis.com or a verified custom domain)
 * only ever serves that courier's storefront pages — EuroCollis platform
 * pages never render there, so every store works from the moment it goes live.
 */
const IS_STOREFRONT_HOST = typeof window !== "undefined" && !!(getStorefrontSubdomain() || getStorefrontCustomHost());
const sfp = (el: React.ReactNode) => <Suspense fallback={<PageLoader />}>{el}</Suspense>;
const StorefrontHostRoutes = () => (
  <Routes>
    <Route path="/auth/confirm" element={sfp(<AuthConfirm />)} />
    <Route path="/auth/continue" element={<StorefrontAuthContinue />} />
    <Route path="/storefront-signin" element={<StorefrontSignIn />} />
    <Route path="/track/:bookingId/:token" element={sfp(<ReceiverChat />)} />
    {["", "/:lang"].flatMap((l) => [
      <Route key={l + "auth"} path={`${l}/auth/:view`} element={sfp(<CourierStorefront />)} />,
      <Route key={l + "route"} path={`${l}/route/:corridor`} element={sfp(<StorefrontCorridor />)} />,
      <Route key={l + "track"} path={`${l}/track`} element={sfp(<StorefrontTrack />)} />,
      <Route key={l + "trackref"} path={`${l}/track/:ref`} element={sfp(<StorefrontTrack />)} />,
      <Route key={l + "q"} path={`${l}/q/:token`} element={sfp(<PublicQuote />)} />,
      <Route key={l + "d"} path={`${l}/d/:token`} element={sfp(<PublicBillingDocument />)} />,
    ])}
    <Route path="*" element={sfp(<CourierStorefront />)} />
  </Routes>
);

import ProtectedRoute from "@/components/auth/ProtectedRoute";
const DevMalakCardsGallery = lazyRetry(() => import("@/pages/DevMalakCardsGallery"), "DevMalakCardsGallery");
const EcSelectDemo = lazyRetry(() => import("@/pages/dev/EcSelectDemo"), "EcSelectDemo");

import { MaintenanceGuard } from "@/components/MaintenanceGuard";
import { SuspensionNotice } from "@/components/SuspensionNotice";

// ── Lazy CRM Portal ─────────────────────────────────────────────
const CrmLayout = lazyRetry(() => import("./crm/CrmLayout"), "CrmLayout");
const CrmProtectedRoute = lazyRetry(() => import("./crm/components/CrmProtectedRoute"), "CrmProtectedRoute");
const CrmDashboard = lazyRetry(() => import("./crm/pages/CrmDashboard"), "CrmDashboard");
const CrmLeadsList = lazyRetry(() => import("./crm/pages/CrmLeadsList"), "CrmLeadsList");
const CrmLeadCreate = lazyRetry(() => import("./crm/pages/CrmLeadCreate"), "CrmLeadCreate");
const CrmLeadDetail = lazyRetry(() => import("./crm/pages/CrmLeadDetail"), "CrmLeadDetail");
const CrmActivitiesList = lazyRetry(() => import("./crm/pages/CrmActivitiesList"), "CrmActivitiesList");
const CrmFollowUpsList = lazyRetry(() => import("./crm/pages/CrmFollowUpsList"), "CrmFollowUpsList");
const CrmMyCallbacks = lazyRetry(() => import("./crm/pages/CrmMyCallbacks"), "CrmMyCallbacks");
const CrmConversionsList = lazyRetry(() => import("./crm/pages/CrmConversionsList"), "CrmConversionsList");
const CrmActivityReport = lazyRetry(() => import("./crm/pages/CrmActivityReport"), "CrmActivityReport");
const CrmSourceReport = lazyRetry(() => import("./crm/pages/CrmSourceReport"), "CrmSourceReport");
const CrmUsersAdmin = lazyRetry(() => import("./crm/pages/CrmUsersAdmin"), "CrmUsersAdmin");
const CrmCourierInvites = lazyRetry(() => import("./crm/pages/CrmCourierInvites"), "CrmCourierInvites");
const CrmAuditLogViewer = lazyRetry(() => import("./crm/pages/CrmAuditLogViewer"), "CrmAuditLogViewer");

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (auth, not found, forbidden)
        if (error instanceof Error && 'status' in error) {
          const status = (error as any).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 2;
      },
      staleTime: 30_000, // 30s before refetch
      refetchOnWindowFocus: false, // Prevent refetch storms
    },
    mutations: {
      retry: false, // Never auto-retry mutations
    },
  },
});

// Reference data (countries, cities, pricing) is restored from the last visit
// before the first render, so repeat visitors paint from cache while React
// Query revalidates in the background.
hydrateQueryCache(queryClient);
startQueryPersistence(queryClient);



/**
 * Renders low-priority chrome only once the browser has gone idle after the
 * first paint. Keeps consent/push/language banners off the critical path.
 */
const DeferredChrome = ({
  push,
  consent,
  language,
}: { push?: boolean; consent?: boolean; language?: boolean }) => {
  const [ready, setReady] = useState(false);
  // Consent is a legal notice: it must show promptly, so it gets a short idle
  // gate of its own. Waiting the full 2.5s made it paint after the page had
  // settled, where it became the largest late paint on the screen.
  const [consentReady, setConsentReady] = useState(false);
  useEffect(() => onIdleOnce(() => setReady(true), 2500), []);
  useEffect(() => onIdleOnce(() => setConsentReady(true), 600), []);
  return (
    <>
      {ready && push && <PushNotificationBanner />}
      {consent && consentReady && <CookieConsentBanner />}
      {ready && language && <LanguageSuggestionBanner />}
    </>
  );
};

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useAuthErrorHandler();
  const { user } = useAuth();

  // Warm the route chunks a visitor is most likely to open next, once the
  // browser is idle. Makes the first in-app navigation feel instant.
  useEffect(() => {
    const handle = prefetchLikelyRoutes({ authed: !!user });
    return () => handle.cancel();
  }, [user]);

  const isLoginRoute = location.pathname === "/login" || /\/[^/]+\/login$/.test(location.pathname);
  const isInstallRoute = location.pathname === "/install" || /\/[^/]+\/install$/.test(location.pathname);
  const showPushBanner = !isLoginRoute && !isInstallRoute;

  // Hard-lock password-recovery flow: if the recovery flag is active, force
  // the user back to /reset-password until they either complete the new
  // password form (which clears the flag) or sign out.
  useEffect(() => {
    let flagged = false;
    try {
      flagged = sessionStorage.getItem("ec_password_recovery_active") === "true";
    } catch {
      flagged = false;
    }
    // Email verification must finish its own safe redirect first; otherwise
    // this guard carries token_hash/next to the reset form instead of redirect.
    const storefrontReset = /\/auth\/reset$/.test(location.pathname);
    if (flagged && location.pathname !== "/reset-password" && location.pathname !== "/auth/confirm" && !storefrontReset) {
      navigate("/reset-password" + location.search + location.hash, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  // Courier-OS shipment invite: signing in / signing up from an invite link must
  // always come back to that exact invite page, never a portal dashboard.
  useEffect(() => {
    const isRoot = location.pathname === "/" || /^\/[a-z]{2}$/.test(location.pathname);
    if (!isRoot) return;
    let raw: string | null = null;
    try { raw = localStorage.getItem("ec_invite_return"); } catch { return; }
    if (!raw) return;
    let parsed: { path?: string; ts?: number } = {};
    try { parsed = JSON.parse(raw); } catch { return; }
    const fresh = typeof parsed.ts === "number" && Date.now() - parsed.ts < 2 * 60 * 60 * 1000;
    const path = parsed.path;
    if (!fresh || !path || !path.startsWith("/s/")) {
      try { localStorage.removeItem("ec_invite_return"); } catch {}
      return;
    }
    const go = () => {
      try { localStorage.removeItem("ec_invite_return"); } catch {}
      navigate(path, { replace: true });
    };
    if (window.location.hash.includes("access_token") || window.location.search.includes("code=")) {
      go();
      return;
    }
    let cancelled = false;
    import("@/integrations/supabase/client").then(({ supabase }) =>
      supabase.auth.getSession().then(({ data }) => {
        if (!cancelled && data.session) go();
      })
    );
    return () => { cancelled = true; };
  }, [location.pathname, navigate]);

  useEffect(() => {
    const updateDir = (lng: string) => {
      const isRTL = lng === 'ar';
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      document.documentElement.lang = lng;
    };
    updateDir(i18n.language);
    i18n.on('languageChanged', updateDir);
    return () => { i18n.off('languageChanged', updateDir); };
  }, []);

  useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;

    const shouldLockHorizontalSwipe = () =>
      document.documentElement.dir === "rtl" &&
      window.matchMedia("(max-width: 1023px)").matches;

    const findAllowedHorizontalScroller = (el: EventTarget | null): HTMLElement | null => {
      let node = el instanceof HTMLElement ? el : null;
      while (node && node !== document.body) {
        if (node.getAttribute("data-allow-horizontal-scroll") === "true") {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    };

    const getNormalizedHorizontalScroll = (el: HTMLElement): { position: number; max: number } => {
      const max = Math.max(0, el.scrollWidth - el.clientWidth);
      if (max <= 0) return { position: 0, max: 0 };

      const direction = getComputedStyle(el).direction;
      const raw = el.scrollLeft;

      if (direction !== "rtl") {
        return { position: Math.max(0, Math.min(raw, max)), max };
      }

      if (raw < 0) {
        return { position: Math.max(0, Math.min(-raw, max)), max };
      }

      return { position: Math.max(0, Math.min(max - raw, max)), max };
    };

    const canScrollInGestureDirection = (el: HTMLElement, fingerDeltaX: number) => {
      const { position, max } = getNormalizedHorizontalScroll(el);
      if (max <= 1) return false;

      const edgeTolerance = 1;
      const movingFingerRight = fingerDeltaX > 0;

      return movingFingerRight
        ? position > edgeTolerance
        : position < max - edgeTolerance;
    };

    const enforceWindowHorizontalLock = () => {
      if (!shouldLockHorizontalSwipe()) return;

      if (window.scrollX !== 0) {
        window.scrollTo({ left: 0, top: window.scrollY, behavior: "auto" });
      }

      if (document.documentElement.scrollLeft !== 0) {
        document.documentElement.scrollLeft = 0;
      }

      if (document.body.scrollLeft !== 0) {
        document.body.scrollLeft = 0;
      }
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (!shouldLockHorizontalSwipe() || event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!shouldLockHorizontalSwipe() || event.touches.length !== 1) return;

      const deltaX = event.touches[0].clientX - touchStartX;
      const deltaY = event.touches[0].clientY - touchStartY;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      if (absDeltaX <= absDeltaY || absDeltaX <= 8) return;

      const horizontalScroller = findAllowedHorizontalScroller(event.target);
      if (!horizontalScroller) {
        event.preventDefault();
        return;
      }

      if (!canScrollInGestureDirection(horizontalScroller, deltaX)) {
        event.preventDefault();
      }
    };

    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("scroll", enforceWindowHorizontalLock, { passive: true });
    requestAnimationFrame(enforceWindowHorizontalLock);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("scroll", enforceWindowHorizontalLock);
    };
  }, []);

  // Affiliate attribution: persist `?ref=CODE` on any landing page so the
  // referral survives until the referred person finishes signing up.
  useEffect(() => {
    captureRefFromUrl(location.search);
  }, [location.search]);

  // …and claim it on the first authenticated load anywhere in the app, not
  // just inside the courier portal. A referred person who signs up and lands
  // on the account/home page was previously never attributed at all.
  useEffect(() => {
    if (!user?.id) return;
    void claimReferralIfPresent(user.id);
  }, [user?.id]);


  useEffect(() => {
    const extractLangFromPath = (pathname: string): string | null => {
      const segments = pathname.split('/').filter(Boolean);
      const firstSegment = segments[0];
      return (SUPPORTED_LANGUAGES as readonly string[]).includes(firstSegment) ? firstSegment : null;
    };
    const langFromRoute = extractLangFromPath(location.pathname);
    if (langFromRoute && langFromRoute !== i18n.language) {
      i18n.changeLanguage(langFromRoute);
    }
  }, [location.pathname]);

  return (
    <>
      {showPushBanner && (
        <Suspense fallback={null}>
          <DeferredChrome push />
        </Suspense>
      )}
      <Suspense fallback={<PageLoader />}>
        {IS_STOREFRONT_HOST ? <StorefrontHostRoutes /> : (
        <Routes>
        <Route path="/dev/malak-cards" element={<Suspense fallback={<PageLoader />}><MalakCardsShowcase /></Suspense>} />

        <Route path="/login" element={<Login />} />
        <Route path="/auth/confirm" element={<Suspense fallback={<PageLoader />}><AuthConfirm /></Suspense>} />
        <Route path="/storefront-signin" element={<StorefrontSignIn />} />
        <Route path="/auth/continue" element={<StorefrontAuthContinue />} />
        <Route path="/auth/:view" element={<Suspense fallback={<PageLoader />}><CourierStorefront /></Suspense>} />
        <Route path="/:slug/auth/:view" element={<Suspense fallback={<PageLoader />}><CourierStorefront /></Suspense>} />
        <Route path="/signup-success" element={<SignupSuccess />} />
        <Route path="/scan/:token" element={<ScanDelivery />} />
        <Route path="/manifest/:token" element={<Suspense fallback={<PageLoader />}><TripManifest /></Suspense>} />
        <Route path="/verify/:evidenceId" element={<Suspense fallback={<PageLoader />}><VerifyDocument /></Suspense>} />
        <Route path="/verify/manifest/:hash" element={<Suspense fallback={<PageLoader />}><VerifyManifest /></Suspense>} />
        {/* Partner pickup portal */}
        <Route path="/partner/login" element={<PartnerLogin />} />
        <Route path="/partner/accept/:token" element={<PartnerAcceptInvite />} />
        <Route path="/partner/join/:code" element={<PartnerJoin />} />
        <Route path="/partner/pending" element={<PartnerPendingApproval />} />
        <Route path="/partner" element={<PartnerLayout />}>
          <Route index element={<PartnerDashboard />} />
          <Route path="scan" element={<PartnerScan />} />
          <Route path="handoff/:id" element={<PartnerHandoffDetail />} />
          <Route path="load" element={<PartnerLoad />} />
          <Route path="history" element={<PartnerHistory />} />
          <Route path="issues" element={<PartnerExceptions />} />

          <Route path="couriers" element={<PartnerCouriers />} />
          <Route path="couriers/:id" element={<PartnerCourierDetail />} />
          <Route path="storefront" element={<PartnerStorefront />} />
          <Route path="profile" element={<PartnerProfile />} />
        </Route>
        {/* Customer-facing pickup landing page (token in URL) — wrapped in main site Layout */}
        <Route element={<RouteErrorBoundary section="EuroCollis"><Layout /></RouteErrorBoundary>}>
          <Route path="/pickup/:token" element={<Suspense fallback={<PageLoader />}><CustomerPickup /></Suspense>} />
        </Route>

        {/* Affiliate referral redirect (anyone scanning /r/CODE) */}
        <Route path="/r/:code" element={<ReferralRedirect />} />


        {/* Post-delivery review link (works signed out; token in URL) */}
        <Route path="/review/:token" element={<Suspense fallback={<PageLoader />}><ReviewByToken /></Suspense>} />

        {/* Receiver Malak — scoped chat for the recipient of a shipment */}
        <Route path="/track/:bookingId/:token" element={<ReceiverChat />} />

        {/* Customer parcel tracking — public, PIN-protected, wrapped in main site Layout */}
        <Route element={<RouteErrorBoundary section="EuroCollis"><Layout /></RouteErrorBoundary>}>
          <Route path="/track" element={<Suspense fallback={<PageLoader />}><TrackLanding /></Suspense>} />
          <Route path="/track/:ref" element={<Suspense fallback={<PageLoader />}><TrackBooking /></Suspense>} />
        </Route>

        {/* Affiliate program — wrapped in main site Layout (navbar + footer) */}
        <Route element={<RouteErrorBoundary section="EuroCollis"><Layout /></RouteErrorBoundary>}>
          <Route path="/affiliates" element={<AffiliatesLanding />} />
        </Route>

        {/* Affiliate portal — standalone layout (own header + sidebar) */}
        {/* Auth is required at the route level (parity with every other
            portal). No `allowedRoles` filter: enrolment into the affiliate
            programme is self-service, so any signed-in user may reach the
            shell; row access stays governed by RLS. */}
        <Route
          element={
            <RouteErrorBoundary section="AffiliatePortal">
              <ProtectedRoute>
                <AffiliateLayout />
              </ProtectedRoute>
            </RouteErrorBoundary>
          }
        >
          <Route path="/affiliate">
            <Route index element={<AffiliateOverview />} />
            <Route path="analytics" element={<AffiliateAnalytics />} />
            <Route path="link" element={<AffiliateLink />} />
            <Route path="referrals" element={<AffiliateReferrals />} />
            <Route path="earnings" element={<AffiliateEarnings />} />
            <Route path="cashout" element={<AffiliateCashout />} />
            <Route path="leaderboard" element={<AffiliateLeaderboard />} />
            <Route path="account" element={<AffiliateAccount />} />
            <Route path="rules" element={<AffiliateRules />} />
            <Route path="assets" element={<AffiliateAssets />} />
          </Route>
        </Route>
        
        {/* Policy Centre */}
        <Route path="/policy-centre" element={<PolicyCentre />} />
        <Route path="/policy-centre/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/policy-centre/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/policy-centre/customer-terms" element={<CustomerTerms />} />
        <Route path="/policy-centre/courier-terms" element={<CourierTermsPage />} />
        <Route path="/policy-centre/prohibited-items" element={<ProhibitedItems />} />
        <Route path="/policy-centre/acceptable-use-policy" element={<AcceptableUsePolicy />} />
        <Route path="/policy-centre/customs-policy" element={<CustomsPolicy />} />
        <Route path="/policy-centre/affiliate-terms" element={<AffiliateTermsPolicy />} />
        <Route path="/policy-centre/dpa" element={<DPAPolicy />} />
        <Route path="/policy-centre/sub-processors" element={<SubProcessorsPolicy />} />
        {/* Public Courier Profile */}
        <Route path="/courier-profile/:courierId" element={<CourierPublicProfile />} />
        {/* Public courier storefront — legacy /c/:slug redirects to flat /:slug */}
        <Route path="/c/:slug" element={<LegacyStorefrontRedirect />} />
        {/* Per-corridor storefront SEO page — /atlastransport/route/casablanca-to-lyon */}
        <Route
          path="/:slug/route/:corridor"
          element={<Suspense fallback={<PageLoader />}><StorefrontCorridor /></Suspense>}
        />
        {/* Branded storefront tracking — /atlastransport/track[/EC-…] */}
        <Route
          path="/:slug/track"
          element={<Suspense fallback={<PageLoader />}><StorefrontTrack /></Suspense>}
        />
        <Route
          path="/:slug/track/:ref"
          element={<Suspense fallback={<PageLoader />}><StorefrontTrack /></Suspense>}
        />
        {/* Courier-authored quote a customer can view and accept */}
        <Route path="/q/:token" element={<Suspense fallback={<PageLoader />}><PublicQuote /></Suspense>} />
        <Route path="/:slug/q/:token" element={<Suspense fallback={<PageLoader />}><PublicQuote /></Suspense>} />

        {/* Receipt or invoice a customer opens from its private link */}
        <Route path="/d/:token" element={<Suspense fallback={<PageLoader />}><PublicBillingDocument /></Suspense>} />
        <Route path="/:slug/d/:token" element={<Suspense fallback={<PageLoader />}><PublicBillingDocument /></Suspense>} />


        {/* Default (English) public routes */}
        <Route element={<RouteErrorBoundary section="EuroCollis"><Layout /></RouteErrorBoundary>}>
          <Route path="/" element={<HostHome />} />
          <Route path="/dev/malak-cards" element={<DevMalakCardsGallery />} />
          <Route path="/dev/select-demo" element={<EcSelectDemo />} />
          <Route path="/dev/gallery-shot" element={<Suspense fallback={<PageLoader />}><DevGalleryShot /></Suspense>} />
          <Route path="/design-preview/transporter" element={<Suspense fallback={<PageLoader />}><DesignPreviewTransporter /></Suspense>} />
          <Route path="/quote" element={<Quote />} />
          <Route path="/results" element={<Results />} />
          <Route path="/booking" element={<Booking />} />
          <Route path="/booking/:bookingId" element={<Booking />} />
          <Route path="/booking-success" element={<BookingSuccess />} />
          <Route path="/confirmation" element={<Confirmation />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/for-couriers" element={<ForCouriers />} />
          <Route path="/courier-os" element={<Suspense fallback={<PageLoader />}><CourierOsLanding /></Suspense>} />
          <Route path="/courier-os/:country" element={<Suspense fallback={<PageLoader />}><CourierOsCountryPage /></Suspense>} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/support" element={<Support />} />
          <Route path="/about" element={<About />} />
            <Route path="/press" element={<Suspense fallback={<PageLoader />}><Press /></Suspense>} />
            <Route path="/benmira-foundation" element={<Suspense fallback={<PageLoader />}><BenmiraFoundation /></Suspense>} />
            
            <Route path="/partnerships" element={<Suspense fallback={<PageLoader />}><Partnerships /></Suspense>} />
            <Route path="/custom-request" element={<CustomRequest />} />
            <Route path="/custom-request/submitted" element={<CustomRequestSubmitted />} />
            <Route path="/offer/:token" element={<GuestOffers />} />
          <Route path="/custom-request-booking" element={<CustomRequestBooking />} />
          <Route path="/reset-pin" element={<ResetPin />} />
          <Route path="/install" element={<Install />} />
          <Route path="/edit-request/:id" element={<EditRequestApproval />} />
          <Route path="/shipping-routes" element={<ShippingRoutesDirectory />} />
          <Route path="/shipping-routes/:slug" element={<ShippingRouteDispatcher />} />
          {/* SEO matrix: /shipping-{item}-{from}-to-{to} → redirect to canonical route */}
          <Route path="/shipping-:slug" element={<ShippingItemRedirect />} />
          {/* Commercial-intent landing pages: "courier to morocco" etc. */}
          <Route path="/courier-to-:country" element={<CourierToCountryPage />} />
          <Route path="/courier-from-:country" element={<CourierToCountryPage />} />
          <Route path="/guides/:slug" element={<GuidePage />} />
          <Route path="/sitemap" element={<HtmlSitemap />} />
          <Route path="/t/:id" element={<TripLanding />} />
          <Route path="/assignment/:token" element={<Suspense fallback={<PageLoader />}><OfflineAssignment /></Suspense>} />
          <Route path="/s/:code" element={<Suspense fallback={<PageLoader />}><ShipmentInvite /></Suspense>} />

          <Route path="/developers/playground" element={<Suspense fallback={<PageLoader />}><DevelopersPlayground /></Suspense>} />
          <Route path="/developers/mcp-docs" element={<Suspense fallback={<PageLoader />}><McpDocs /></Suspense>} />
          <Route path="/developers/install" element={<Suspense fallback={<PageLoader />}><McpInstall /></Suspense>} />

          <Route path="/developers" element={<Suspense fallback={<PageLoader />}><DevelopersPortal /></Suspense>} />
          <Route path="/developers/auth" element={<Suspense fallback={<PageLoader />}><DevelopersAuth /></Suspense>} />
          <Route path="/reset-password" element={<Suspense fallback={<PageLoader />}><ResetPassword /></Suspense>} />
          <Route path="/courier/setup" element={<Suspense fallback={<PageLoader />}><CourierSetup /></Suspense>} />
          <Route path="/forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPassword /></Suspense>} />
          <Route
            path="/developers/dashboard"
            element={
              <Suspense fallback={<PageLoader />}>
                <DeveloperAuthGuard>
                  <DevelopersDashboard />
                </DeveloperAuthGuard>
              </Suspense>
            }
          />
          <Route path="/developers/openapi" element={<Suspense fallback={<PageLoader />}><OpenApiDocs /></Suspense>} />
          <Route path="/developers/errors" element={<Suspense fallback={<PageLoader />}><DevelopersPortal /></Suspense>} />
          <Route path="/developers/mcp" element={<Navigate to="/developers/playground?tab=mcp" replace />} />
          <Route path="/trust" element={<Suspense fallback={<PageLoader />}><TrustCenter /></Suspense>} />
          <Route path="/status" element={<Suspense fallback={<PageLoader />}><StatusPage /></Suspense>} />
        </Route>
        {/* Localized public routes */}
        <Route path="/:lang" element={<LanguageRoute />}>
          <Route path="login" element={<Login />} />
          <Route path="signup-success" element={<SignupSuccess />} />
          <Route element={<RouteErrorBoundary section="EuroCollis"><Layout /></RouteErrorBoundary>}>
            <Route index element={<HostHome />} />
            <Route path="quote" element={<Quote />} />
            <Route path="results" element={<Results />} />
            <Route path="booking" element={<Booking />} />
            <Route path="booking/:bookingId" element={<Booking />} />
            <Route path="booking-success" element={<BookingSuccess />} />
            <Route path="confirmation" element={<Confirmation />} />
            <Route path="how-it-works" element={<HowItWorks />} />
            <Route path="for-couriers" element={<ForCouriers />} />
            <Route path="courier-os" element={<Suspense fallback={<PageLoader />}><CourierOsLanding /></Suspense>} />
            <Route path="courier-os/:country" element={<Suspense fallback={<PageLoader />}><CourierOsCountryPage /></Suspense>} />
            <Route path="faq" element={<FAQ />} />
            <Route path="support" element={<Support />} />
            <Route path="about" element={<About />} />
            <Route path="press" element={<Suspense fallback={<PageLoader />}><Press /></Suspense>} />
            <Route path="benmira-foundation" element={<Suspense fallback={<PageLoader />}><BenmiraFoundation /></Suspense>} />
            
            <Route path="partnerships" element={<Suspense fallback={<PageLoader />}><Partnerships /></Suspense>} />
            <Route path="custom-request" element={<CustomRequest />} />
            <Route path="custom-request/submitted" element={<CustomRequestSubmitted />} />
            <Route path="offer/:token" element={<GuestOffers />} />
            <Route path="custom-request-booking" element={<CustomRequestBooking />} />
            <Route path="reset-pin" element={<ResetPin />} />
            <Route path="install" element={<Install />} />
            <Route path="shipping-routes" element={<ShippingRoutesDirectory />} />
            <Route path="shipping-routes/:slug" element={<ShippingRouteDispatcher />} />
            <Route path="guides/:slug" element={<GuidePage />} />
            <Route path="compare" element={<PrerenderedFallback kind="compare" />} />
            <Route path="compare/:slug" element={<PrerenderedFallback kind="compare" />} />
            <Route path="glossary" element={<PrerenderedFallback kind="glossary" />} />
            <Route path="glossary/:slug" element={<PrerenderedFallback kind="glossary" />} />
            <Route path="t/:id" element={<TripLanding />} />
            <Route path="policy-centre" element={<PolicyCentre />} />
            <Route path="policy-centre/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="policy-centre/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="policy-centre/customer-terms" element={<CustomerTerms />} />
            <Route path="policy-centre/courier-terms" element={<CourierTermsPage />} />
            <Route path="policy-centre/prohibited-items" element={<ProhibitedItems />} />
            <Route path="policy-centre/acceptable-use-policy" element={<AcceptableUsePolicy />} />
            <Route path="policy-centre/customs-policy" element={<CustomsPolicy />} />
            <Route path="policy-centre/affiliate-terms" element={<AffiliateTermsPolicy />} />
            <Route path="policy-centre/dpa" element={<DPAPolicy />} />
            <Route path="policy-centre/sub-processors" element={<SubProcessorsPolicy />} />
            <Route path="courier-profile/:courierId" element={<CourierPublicProfile />} />
            <Route path="affiliates" element={<AffiliatesLanding />} />
            <Route path="edit-request/:id" element={<EditRequestApproval />} />
            <Route path="shipping-:slug" element={<ShippingItemRedirect />} />
            <Route path="sitemap" element={<HtmlSitemap />} />
            <Route path="trust" element={<Suspense fallback={<PageLoader />}><TrustCenter /></Suspense>} />
            <Route path="status" element={<Suspense fallback={<PageLoader />}><StatusPage /></Suspense>} />
            <Route path="reset-password" element={<Suspense fallback={<PageLoader />}><ResetPassword /></Suspense>} />
            <Route path="forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPassword /></Suspense>} />
            <Route path="developers" element={<Suspense fallback={<PageLoader />}><DevelopersPortal /></Suspense>} />
            <Route path="developers/auth" element={<Suspense fallback={<PageLoader />}><DevelopersAuth /></Suspense>} />
            <Route path="developers/playground" element={<Suspense fallback={<PageLoader />}><DevelopersPlayground /></Suspense>} />
            <Route path="developers/mcp-docs" element={<Suspense fallback={<PageLoader />}><McpDocs /></Suspense>} />
            <Route path="developers/install" element={<Suspense fallback={<PageLoader />}><McpInstall /></Suspense>} />

            <Route path="developers/openapi" element={<Suspense fallback={<PageLoader />}><OpenApiDocs /></Suspense>} />
            <Route path="developers/errors" element={<Suspense fallback={<PageLoader />}><DevelopersPortal /></Suspense>} />
            <Route path="developers/mcp" element={<Navigate to="/developers/playground?tab=mcp" replace />} />
            <Route
              path="developers/dashboard"
              element={
                <Suspense fallback={<PageLoader />}>
                  <DeveloperAuthGuard>
                    <DevelopersDashboard />
                  </DeveloperAuthGuard>
                </Suspense>
              }
            />
          </Route>
          {/* Localized courier storefront (/fr/atlastransport …) — the real
              hreflang target for each language variant. Declared last so every
              static localized path above wins the match. */}
          <Route path=":slug/auth/:view" element={<Suspense fallback={<PageLoader />}><CourierStorefront /></Suspense>} />
          <Route
            path=":slug/route/:corridor"
            element={
              <Suspense fallback={<PageLoader />}>
                <StorefrontCorridor />
              </Suspense>
            }
          />
          <Route
            path=":slug/track"
            element={
              <Suspense fallback={<PageLoader />}>
                <StorefrontTrack />
              </Suspense>
            }
          />
          <Route
            path=":slug/track/:ref"
            element={
              <Suspense fallback={<PageLoader />}>
                <StorefrontTrack />
              </Suspense>
            }
          />
          <Route
            path=":slug"
            element={
              <Suspense fallback={<PageLoader />}>
                <CourierStorefront />
              </Suspense>
            }
          />
        </Route>
        {/* Customer Portal */}
        <Route
          path="/account"
          element={
            <ProtectedRoute allowedRoles={["customer"]}>
              <RouteErrorBoundary section="My Account" fallbackPath="/account">
                <Suspense fallback={<PortalSkeleton />}>
                  <AccountLayout />
                </Suspense>
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        >
          <Route index element={<AccountDashboard />} />
          <Route path="bookings" element={<MyBookings />} />
          <Route path="bookings/:id" element={<BookingDetail />} />
          <Route path="requests" element={<ShipmentRequests />} />
          <Route path="requests/:id" element={<ShipmentRequestDetail />} />
          <Route path="offers/:bidId" element={<OfferDetail />} />
          <Route path="offers" element={<MyOffers />} />
          <Route path="profile" element={<AccountProfile />} />
          <Route path="address-book" element={<AddressBook />} />
          <Route path="transporters" element={<MyTransporters />} />
          <Route path="deposit/:id" element={<DepositPayment />} />
          <Route path="messages" element={<Messages />} />
          <Route path="messages/new" element={<Messages />} />
          <Route path="messages/:conversationId" element={<Messages />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="notifications/settings" element={<AccountNotificationSettings />} />
          <Route path="support" element={<AccountSupport />} />
          <Route path="disputes" element={<MyDisputes />} />
          <Route path="disputes/:id" element={<CustomerDisputeDetail />} />
        </Route>
        {/* Driver workspace — a simplified entry point into the existing Courier OS. */}
        <Route
          path="/driver"
          element={
            <ProtectedRoute allowedRoles={["courier"]}>
              <RouteErrorBoundary section="Driver" fallbackPath="/driver">
                <Suspense fallback={<PortalSkeleton />}>
                  <DriverHome />
                </Suspense>
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        {/* Courier team invitations — the invitee may not be a courier yet. */}
        <Route path="/team/join/:token" element={<CourierTeamJoin />} />
        {/* Courier Portal */}
        <Route
          path="/courier"
          element={
            <ProtectedRoute allowedRoles={["courier"]}>
              <RouteErrorBoundary section="Courier Portal" fallbackPath="/courier">
                <Suspense fallback={<PortalSkeleton />}>
                  <CourierLayout />
                </Suspense>
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="onboarding" element={<CourierOnboarding />} />
          <Route path="trips" element={<RequirePermission perm="trips.view"><Trips /></RequirePermission>} />
          <Route path="trips/create" element={<RequirePermission perm="trips.manage"><CreateTrip /></RequirePermission>} />
          <Route path="trips/:id" element={<RequirePermission perm="trips.view"><TripDetail /></RequirePermission>} />
          <Route path="trips/:id/execute" element={<RequirePermission perm="trips.view"><TripExecute /></RequirePermission>} />
          <Route path="trips/:id/performance" element={<TripPerformanceRedirect />} />
          <Route path="analytics" element={<RequirePermission perm="analytics.view"><CourierAnalytics /></RequirePermission>} />
          <Route path="finance" element={<RequirePermission perm="finance.view"><CourierFinance /></RequirePermission>} />
          <Route path="earnings" element={<Navigate to="../finance" replace />} />
          <Route path="fleet" element={<RequirePermission perm="fleet.view"><CourierFleet /></RequirePermission>} />
          <Route path="trips/:id/edit" element={<RequirePermission perm="trips.manage"><EditTrip /></RequirePermission>} />
          <Route path="bookings" element={<RequirePermission perm="shipments.view"><CourierBookings /></RequirePermission>} />
          <Route path="bookings/direct/new" element={<RequirePermission perm="shipments.edit"><DirectShipmentNew /></RequirePermission>} />

          <Route path="bookings/:id" element={<RequirePermission perm="shipments.view"><CourierBookingDetail /></RequirePermission>} />
          <Route path="open/:kind/:id" element={<RequirePermission perm="shipments.view"><OpenDocumentLink /></RequirePermission>} />
          <Route path="contacts" element={<RequirePermission perm="contacts.view"><CourierContacts /></RequirePermission>} />
          <Route path="contacts/:id" element={<RequirePermission perm="contacts.view"><CourierContactDetail /></RequirePermission>} />
          <Route path="match-offers" element={<Navigate to="/courier/jobs?tab=matches" replace />} />
          <Route path="match-offers/:id" element={<RequirePermission perm="jobs.view"><MatchOfferDetail /></RequirePermission>} />
          <Route path="pricing" element={<RequirePermission perm="pricing.manage"><PricingTemplates /></RequirePermission>} />
          <Route path="team" element={<RequirePermission perm="team.manage"><CourierTeam /></RequirePermission>} />
          <Route path="routes" element={<RequirePermission perm="routes.manage"><RouteTemplatesPage /></RequirePermission>} />
          <Route path="jobs" element={<RequirePermission perm="jobs.view"><Jobs /></RequirePermission>} />
          <Route path="jobs/:id" element={<RequirePermission perm="jobs.view"><JobDetail /></RequirePermission>} />
          <Route path="messages" element={<RequirePermission perm="messages.view"><CourierMessages /></RequirePermission>} />
          <Route path="messages/new" element={<RequirePermission perm="messages.view"><CourierMessages /></RequirePermission>} />
          <Route path="messages/:conversationId" element={<RequirePermission perm="messages.view"><CourierMessages /></RequirePermission>} />
          <Route path="profile" element={<Profile />} />
          <Route path="reviews" element={<CourierReviewsPage />} />
          <Route path="storefront" element={<StorefrontBuilder />} />
          <Route path="storefront/builder" element={<StorefrontBuilder />} />
          <Route path="storefront/leads" element={<Navigate to="/courier/contacts?tab=enquiries" replace />} />
          <Route path="notifications" element={<CourierNotifications />} />
          <Route path="notifications/settings" element={<CourierNotificationSettings />} />
          <Route path="support" element={<CourierSupport />} />
          <Route path="guides" element={<CourierGuides />} />
          <Route path="disputes" element={<CourierDisputes />} />
          <Route path="disputes/:id" element={<CourierDisputeDetail />} />
          <Route path="promotions" element={<CourierPromotions />} />
          <Route path="partners" element={<Partners />} />
          <Route path="agency-assignments" element={<AgencyAssignments />} />
          <Route path="bulk-dropoff" element={<BulkDropoff />} />
          <Route path="documents" element={<CourierDocuments />} />
          <Route path="settings" element={<CourierSettings />} />
          <Route path="documents/trip/:id" element={<CourierDocumentsTrip />} />
        </Route>
        {/* Admin Panel */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <RouteErrorBoundary section="Admin Panel" fallbackPath="/admin">
                <Suspense fallback={<PortalSkeleton />}>
                  <AdminLayout />
                </Suspense>
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/:id" element={<AdminUserDetail />} />
          <Route path="couriers" element={<AdminCouriers />} />
          <Route path="couriers/:id" element={<AdminCourierDetail />} />
          <Route path="routes" element={<AdminRoutes />} />
          <Route path="exceptions" element={<AdminExceptions />} />
          <Route path="teams" element={<AdminTeams />} />
          <Route path="crm" element={<AdminCrm />} />
          <Route path="storefronts" element={<AdminStorefronts />} />
          <Route path="domains" element={<AdminDomains />} />
          <Route path="contacts" element={<AdminContacts />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="data-integrity" element={<AdminReconciliation />} />
          <Route path="routes/:id" element={<AdminRouteDetail />} />
          <Route path="pricing" element={<AdminPricingTemplates />} />
          <Route path="pricing/:id" element={<AdminPricingTemplateDetail />} />
          <Route path="trips" element={<AdminTrips />} />
          <Route path="trips/:id" element={<AdminTripDetail />} />
          <Route path="requests" element={<AdminRequests />} />
          <Route path="requests/:id" element={<AdminRequestDetail />} />
          <Route path="bids" element={<AdminBids />} />
          <Route path="bids/:id" element={<AdminBidDetail />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="bookings/:id" element={<AdminBookingDetail />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="currencies" element={<AdminCurrencies />} />
          <Route path="payments/:id" element={<AdminPaymentDetail />} />
          <Route path="disputes" element={<AdminDisputes />} />
          <Route path="disputes/:id" element={<AdminDisputeDetail />} />
          <Route path="support" element={<AdminSupportTickets />} />
          <Route path="support/:id" element={<AdminTicketDetail />} />
          <Route path="support-analytics" element={<AdminSupportAnalytics />} />
          <Route path="opening-hours" element={<AdminOpeningHours />} />
          <Route path="logs" element={<AdminLogs />} />
          <Route path="scan-logs" element={<AdminScanLogs />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="applications" element={<AdminApplications />} />
          <Route path="documents" element={<AdminDocuments />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="emails" element={<AdminEmails />} />
          <Route path="items" element={<AdminItems />} />
          <Route path="ai-learning" element={<AdminAILearning />} />
          <Route path="malak-ops" element={<AdminMalakOps />} />
          <Route path="cost-counters" element={<AdminCostCounters />} />
          <Route path="ai-cap-monitor" element={<AdminAICapMonitor />} />
          <Route path="malak-settings" element={<AdminMalakSettings />} />
          <Route path="csat" element={<AdminCSAT />} />
          <Route path="learning-pipe" element={<Navigate to="/admin/ai-learning" replace />} />
          <Route path="correction-moderation" element={<AdminCorrectionModeration />} />
          <Route path="cost-attribution" element={<AdminCostAttribution />} />
          <Route path="audit-trail" element={<AdminAuditTrail />} />
          <Route path="ai-margin" element={<AdminAIMargin />} />
          <Route path="malak-regression" element={<AdminMalakRegression />} />
          <Route path="items-catalog" element={<AdminItemsCatalog />} />
          <Route path="edit-moderation" element={<AdminEditModeration />} />
          <Route path="field-policies" element={<AdminFieldPolicies />} />
          <Route path="kb-freshness" element={<AdminKBFreshness />} />
          <Route path="kb-retrieval" element={<AdminKBRetrieval />} />
          <Route path="kb-review-queue" element={<Navigate to="/admin/ai-learning?tab=review" replace />} />
          <Route path="malak-trace" element={<AdminMalakTrace />} />
          <Route path="live-chat" element={<Navigate to="/admin/live-support" replace />} />
          <Route path="live-support" element={<AdminLiveSupport />} />
          <Route path="supervisor" element={<AdminSupervisor />} />
          <Route path="stress-test" element={<AdminStressTest />} />
          <Route path="webhook-retries" element={<AdminWebhookRetries />} />
          <Route path="promo-codes" element={<AdminPromoCodes />} />
          <Route path="locations" element={<AdminLocations />} />
          <Route path="corridors" element={<AdminCorridors />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="monitoring" element={<Navigate to="/admin/monitor?tab=system" replace />} />
          <Route path="monitor" element={<AdminMonitor />} />
          <Route path="moderation" element={<AdminModeration />} />
          <Route path="conversations" element={<SupportConversations />} />
          <Route path="conversations/:id" element={<SupportConversationDetail />} />
          <Route path="ai-security" element={<AdminAISecurity />} />
          <Route path="malak-chats" element={<AdminMalakChats />} />
          <Route path="malak-funnel" element={<AdminMalakFunnel />} />
          <Route path="foundation" element={<AdminFoundation />} />
          <Route path="affiliates" element={<AdminAffiliates />} />
          <Route path="affiliates/:id" element={<AdminAffiliateDetail />} />
          <Route path="affiliate-assets" element={<AdminAffiliateAssets />} />
          <Route path="affiliate-cashouts" element={<AdminAffiliateCashouts />} />
          <Route path="referral-seasons" element={<AdminReferralSeasons />} />
          <Route path="partners" element={<AdminPartners />} />
          <Route path="partners/:id" element={<AdminPartnerDetail />} />
        </Route>
        {/* Support Portal */}
        <Route
          path="/support-portal"
          element={
            <ProtectedRoute allowedRoles={["support"]}>
              <RouteErrorBoundary section="Support Portal" fallbackPath="/support-portal">
                <Suspense fallback={<PortalSkeleton />}>
                  <SupportPortalLayout />
                </Suspense>
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        >
          <Route index element={<SupportDashboard />} />
          <Route path="customers" element={<SupportCustomers />} />
          <Route path="customers/:id" element={<SupportCustomerDetail />} />
          <Route path="couriers" element={<SupportCouriers />} />
          <Route path="couriers/:id" element={<SupportCourierDetail />} />
          <Route path="bookings" element={<SupportBookings />} />
          <Route path="bookings/:id" element={<SupportBookingDetail />} />
          <Route path="requests" element={<SupportRequests />} />
          <Route path="requests/:id" element={<SupportRequestDetail />} />
          <Route path="bids" element={<SupportBids />} />
          <Route path="trips" element={<SupportTrips />} />
          <Route path="routes" element={<SupportRoutes />} />
          <Route path="tickets" element={<SupportTickets />} />
          <Route path="tickets/:id" element={<SupportTicketDetail />} />
          <Route path="live-chat" element={<Navigate to="/support-portal/live-support" replace />} />
          <Route path="live-support" element={<SupportLiveSupport />} />
          
          <Route path="payments" element={<SupportPayments />} />
          <Route path="payments/:id" element={<SupportPaymentDetail />} />
          <Route path="disputes" element={<SupportDisputes />} />
          <Route path="disputes/:id" element={<SupportDisputeDetail />} />
          <Route path="verification-funnel" element={<SupportVerificationFunnel />} />
          <Route path="agent-analytics" element={<SupportAgentAnalytics />} />
          <Route path="blocked-users" element={<SupportBlockedUsers />} />
          {/* Shared staff surfaces — same components as admin, portal-aware links. */}
          <Route path="affiliates" element={<AdminAffiliates />} />
          <Route path="affiliates/:id" element={<AdminAffiliateDetail />} />
          <Route path="partners" element={<AdminPartners />} />
          <Route path="partners/:id" element={<AdminPartnerDetail />} />
          <Route path="conversations" element={<SupportConversations />} />
          <Route path="conversations/:id" element={<SupportConversationDetail />} />
          {/* Embedded Malak workspace — MalakChatWithProactive hides the floating widget here. */}
          <Route path="ask-malak" element={<SupportAskMalak />} />
        </Route>
        {/* Internal Sales CRM Portal */}
        <Route
          path="/crm"
          element={
            <Suspense fallback={<PortalSkeleton />}>
              <CrmProtectedRoute>
                <RouteErrorBoundary section="CRM" fallbackPath="/crm">
                  <CrmLayout />
                </RouteErrorBoundary>
              </CrmProtectedRoute>
            </Suspense>
          }
        >
          <Route index element={<CrmDashboard />} />
          <Route path="pipeline" element={<Navigate to="/crm/leads" replace />} />
          <Route path="leads" element={<CrmLeadsList />} />
          <Route path="leads/new" element={<CrmLeadCreate />} />
          <Route path="leads/:id" element={<CrmLeadDetail />} />
          <Route path="activities" element={<CrmActivitiesList />} />
          <Route path="follow-ups" element={<CrmFollowUpsList />} />
          <Route path="my-callbacks" element={<CrmMyCallbacks />} />
          <Route path="conversions" element={<CrmConversionsList />} />
          <Route path="reports/activity" element={<CrmActivityReport />} />
          <Route path="reports/sources" element={<CrmSourceReport />} />
          <Route path="courier-invites" element={<CrmCourierInvites />} />
          <Route path="admin/users" element={
            <Suspense fallback={<PortalSkeleton />}>
              <CrmProtectedRoute requireRole="crm_admin"><CrmUsersAdmin /></CrmProtectedRoute>
            </Suspense>
          } />
          <Route path="admin/audit" element={
            <Suspense fallback={<PortalSkeleton />}>
              <CrmProtectedRoute requireRole={["crm_admin","sales_manager"]}><CrmAuditLogViewer /></CrmProtectedRoute>
            </Suspense>
          } />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
        )}
    </Suspense>
    </>
  );
};

const App = () => (
  <HelmetProvider>
    <GlobalErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <MalakProvider>
              
                <CurrencyProvider>
                  <QuoteProvider>

                    <Sonner />
                    <BrowserRouter>
                      <MaintenanceGuard>
                        <SuspensionNotice>
                          <Suspense fallback={null}>
                            <DeferredChrome consent language />
                          </Suspense>
                          <ScrollToTop />
                          <NativeBackButton />
                          <AppContent />
                          <MalakChatWithProactive />
                          
                        </SuspensionNotice>
                      </MaintenanceGuard>
                    </BrowserRouter>
                  </QuoteProvider>
                </CurrencyProvider>
              
            </MalakProvider>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </GlobalErrorBoundary>
  </HelmetProvider>
);

export default App;
